import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { GameComponentProps } from '../core/types';
import { isWordyPuzzle, type WordyPuzzle } from './generate';
import {
  buildKeyboardState,
  evaluateGuess,
  getWordyScore,
  isAcceptedGuess,
  type LetterState,
} from './logic';

type WordySavedState = {
  version: 1;
  guesses: string[];
  currentGuess: string;
  revealCompleteCount: number;
};

const keyboardRows = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
const revealDurationMs = 980;
const solvedRevealDurationMs = 1_450;

function readSavedState(value: unknown): WordySavedState {
  if (!value || typeof value !== 'object') {
    return { version: 1, guesses: [], currentGuess: '', revealCompleteCount: 0 };
  }
  const candidate = value as Partial<WordySavedState>;
  const guesses = Array.isArray(candidate.guesses)
    ? candidate.guesses.filter((word): word is string => typeof word === 'string').slice(0, 6)
    : [];
  const currentGuess =
    typeof candidate.currentGuess === 'string'
      ? candidate.currentGuess
          .toLowerCase()
          .replace(/[^a-z]/g, '')
          .slice(0, 5)
      : '';
  const revealCompleteCount =
    typeof candidate.revealCompleteCount === 'number'
      ? Math.max(0, Math.min(guesses.length, Math.floor(candidate.revealCompleteCount)))
      : guesses.length;
  return { version: 1, guesses, currentGuess, revealCompleteCount };
}

export function WordyGame({
  puzzle,
  savedState,
  disabled,
  reducedMotion,
  onStateChange,
  onComplete,
}: GameComponentProps) {
  if (!isWordyPuzzle(puzzle.payload)) throw new Error('Wordy received an invalid puzzle payload.');
  const wordyPuzzle: WordyPuzzle = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const [guesses, setGuesses] = useState(initial.guesses);
  const [currentGuess, setCurrentGuess] = useState(initial.currentGuess);
  const [revealCompleteCount, setRevealCompleteCount] = useState(initial.revealCompleteCount);
  const [message, setMessage] = useState('');
  const [shakeRevision, setShakeRevision] = useState(0);
  const [isRevealing, setIsRevealing] = useState(
    initial.guesses.length > initial.revealCompleteCount,
  );

  const keyboardState = useMemo(
    () => buildKeyboardState(guesses, wordyPuzzle.answer),
    [guesses, wordyPuzzle.answer],
  );

  const persist = useCallback(
    (nextGuesses: string[], nextCurrentGuess: string, nextRevealCompleteCount: number) => {
      onStateChange({
        version: 1,
        guesses: nextGuesses,
        currentGuess: nextCurrentGuess,
        revealCompleteCount: nextRevealCompleteCount,
      });
    },
    [onStateChange],
  );

  const updateCurrentGuess = useCallback(
    (next: string) => {
      setCurrentGuess(next);
      setMessage('');
      persist(guesses, next, revealCompleteCount);
    },
    [guesses, persist, revealCompleteCount],
  );

  const submit = useCallback(() => {
    if (disabled || isRevealing || guesses.length >= 6) return;
    if (currentGuess.length !== 5) {
      setMessage('Not enough letters');
      setShakeRevision((revision) => revision + 1);
      return;
    }
    if (!isAcceptedGuess(currentGuess)) {
      setMessage('Not in word list');
      setShakeRevision((revision) => revision + 1);
      return;
    }
    const nextGuesses = [...guesses, currentGuess];
    setIsRevealing(true);
    setGuesses(nextGuesses);
    setCurrentGuess('');
    setMessage('');
    persist(nextGuesses, '', revealCompleteCount);
  }, [currentGuess, disabled, guesses, isRevealing, persist, revealCompleteCount]);

  const handleKey = useCallback(
    (key: string) => {
      if (disabled || isRevealing) return;
      if (key === 'Enter') {
        submit();
        return;
      }
      if (key === 'Backspace') {
        updateCurrentGuess(currentGuess.slice(0, -1));
        return;
      }
      if (/^[a-z]$/i.test(key) && currentGuess.length < 5) {
        updateCurrentGuess(`${currentGuess}${key.toLowerCase()}`);
      }
    },
    [currentGuess, disabled, isRevealing, submit, updateCurrentGuess],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Enter' || event.key === 'Backspace' || /^[a-z]$/i.test(event.key)) {
        event.preventDefault();
        handleKey(event.key);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleKey]);

  useEffect(() => {
    if (guesses.length === 0 || guesses.length <= revealCompleteCount) {
      setIsRevealing(false);
      return;
    }
    const lastGuess = guesses.at(-1)!;
    setIsRevealing(true);
    const handle = window.setTimeout(
      () => {
        const solved = lastGuess === wordyPuzzle.answer;
        if (solved || guesses.length === 6) {
          onComplete({
            kind: solved ? 'solved' : 'failed',
            baseDeltaMs: getWordyScore(guesses.length, solved),
            message: solved ? 'Brilliant!' : `The word was ${wordyPuzzle.answer.toUpperCase()}.`,
            metadata: {
              guessCount: guesses.length,
              guesses,
              answer: wordyPuzzle.answer,
            },
          });
        } else {
          setRevealCompleteCount(guesses.length);
          persist(guesses, '', guesses.length);
          setIsRevealing(false);
        }
      },
      reducedMotion
        ? 50
        : lastGuess === wordyPuzzle.answer
          ? solvedRevealDurationMs
          : revealDurationMs,
    );
    return () => window.clearTimeout(handle);
  }, [guesses, onComplete, persist, reducedMotion, revealCompleteCount, wordyPuzzle.answer]);

  const activeRow = guesses.length;
  const lastGuessSolved = guesses.at(-1) === wordyPuzzle.answer;

  return (
    <section className="wordy-game" aria-labelledby="wordy-title">
      <p className="eyebrow">Six guesses</p>
      <h1 id="wordy-title" className="sr-only">
        Wordy
      </h1>
      <div className="wordy-board" role="grid" aria-label="Wordy board, six rows by five columns">
        {Array.from({ length: 6 }, (_, rowIndex) => {
          const submittedGuess = guesses[rowIndex];
          const letters = submittedGuess ?? (rowIndex === activeRow ? currentGuess : '');
          const evaluation = submittedGuess
            ? evaluateGuess(wordyPuzzle.answer, submittedGuess)
            : undefined;
          const revealing = isRevealing && rowIndex === guesses.length - 1;
          const solvedRow = lastGuessSolved && rowIndex === guesses.length - 1;
          return (
            <div
              className={`wordy-row ${
                rowIndex === activeRow && shakeRevision ? 'wordy-row--shake' : ''
              } ${solvedRow ? 'wordy-row--solved' : ''}`}
              key={`${rowIndex}:${rowIndex === activeRow ? shakeRevision : 0}`}
              role="row"
              aria-label={`Guess ${rowIndex + 1}`}
            >
              {Array.from({ length: 5 }, (_, columnIndex) => {
                const letter = letters[columnIndex] ?? '';
                const state = evaluation?.[columnIndex];
                return (
                  <div
                    key={columnIndex}
                    role="gridcell"
                    aria-label={tileLabel(rowIndex, columnIndex, letter, state)}
                    className={`wordy-tile ${letter ? 'wordy-tile--filled' : ''} ${
                      state ? `wordy-tile--${state}` : ''
                    } ${revealing ? 'wordy-tile--revealing' : ''}`}
                    style={{ '--tile-delay': `${columnIndex * 100}ms` } as CSSProperties}
                  >
                    <span>{letter}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="wordy-message" role="status" aria-live="polite">
        {message}
      </div>

      <div className="wordy-keyboard" aria-label="Wordy keyboard">
        {keyboardRows.map((row, rowIndex) => (
          <div className="wordy-keyboard-row" key={row}>
            {rowIndex === 2 && (
              <button
                className="wordy-key wordy-key--wide"
                disabled={disabled || isRevealing || currentGuess.length === 0}
                aria-label="Backspace"
                onClick={() => handleKey('Backspace')}
              >
                ⌫
              </button>
            )}
            {row.split('').map((letter) => (
              <button
                key={letter}
                className={`wordy-key ${
                  keyboardState[letter] ? `wordy-key--${keyboardState[letter]}` : ''
                }`}
                disabled={disabled || isRevealing}
                aria-label={keyLabel(letter, keyboardState[letter])}
                onClick={() => handleKey(letter)}
              >
                {letter}
              </button>
            ))}
            {rowIndex === 2 && (
              <button
                className="wordy-key wordy-key--wide"
                disabled={disabled || isRevealing || currentGuess.length !== 5}
                onClick={() => handleKey('Enter')}
              >
                Enter
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function tileLabel(
  rowIndex: number,
  columnIndex: number,
  letter: string,
  state?: LetterState,
): string {
  const contents = letter ? `letter ${letter.toUpperCase()}` : 'empty';
  return `Row ${rowIndex + 1} column ${columnIndex + 1}, ${contents}${state ? `, ${state}` : ''}`;
}

function keyLabel(letter: string, state?: LetterState): string {
  return state ? `${letter.toUpperCase()}, ${state}` : letter.toUpperCase();
}
